import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vShow as _vShow, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "md:flex md:items-center md:justify-end bg-white border-b border-gray-200 flex py-4 pl-8" }
const _hoisted_2 = { class: "max-w-lg w-full md:w-56 lg:max-w-xs" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("label", {
  for: "search",
  class: "sr-only"
}, "Search", -1)
const _hoisted_4 = { class: "relative text-gray-400 focus-within:text-gray-600" }
const _hoisted_5 = { class: "pointer-events-none absolute inset-y-0 pl-3 left-0 flex items-center" }
const _hoisted_6 = ["placeholder"]
const _hoisted_7 = { class: "flex flex-col md:flex-row mx-auto md:mr-auto md:mx-0" }
const _hoisted_8 = { class: "mt-4 flex-shrink-0 flex md:mt-0 md:ml-4 md:pr-8 mx-auto" }
const _hoisted_9 = { class: "mt-4 flex-shrink-0 flex md:mt-0 md:ml-4 pr-8" }
const _hoisted_10 = { class: "px-8 py-6" }
const _hoisted_11 = { class: "space-y-8 divide-y divide-gray-200 bg-white px-10 py-9 rounded-md shadow-md text-center" }
const _hoisted_12 = { class: "py-6" }
const _hoisted_13 = { class: "sm:mx-6 md:mx-8 bg-white shadow overflow-x-auto sm:rounded-md" }
const _hoisted_14 = { class: "shadow overflow-x-auto border-b border-gray-200 sm:rounded-lg" }

import "dayjs/locale/th";
import dayjs from "dayjs";
import stringify from "qs-stringify";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { AxiosInstance } from "axios";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { inject, onMounted, ref, watch, shallowRef } from "vue";
import Navbar from "@/components/tools/Navbar.vue";
import DataTable from "@/components/tools/DataTable.vue";
import Pagination from "@/components/tools/Pagination.vue";
import { formatNumber, translate } from "@/utils/tools";
import { formatP2PayWithdrawMessage } from "@/utils/p2pay-withdraw-message";
import {
  getWithdrawDisplayAmount,
  getWithdrawDisplayFinalAmount,
  getWithdrawDisplayStatus,
} from "@/utils/p2pay-withdraw-display";
import {
  buildP2PayCancellationRequest,
  canRequestP2PayCancellation,
  getP2PayCancellationOutcome,
  getP2PayCmReviewReasonCode,
  isP2PayWithdrawPaymentName,
} from "@/utils/p2pay-withdraw-review-action";
import type {
  P2PayCancellationOutcome,
} from "@/utils/p2pay-withdraw-review-action";

import Toast from "@/components/tools/Toast.vue";
import { WithdrawPagination } from "./types/withdraw.type";
import DateSelector from "@/components/tools/DateTimeSelector.vue";
import WithdrawPaymentSwitchingFilterDialog from "@/components/dialogs/WithdrawPaymentSwitchingFilterDialog.vue";
import ExportTransactionDialog from "@/components/dialogs/ExportTransactionDialog.vue";
import PopOver from "@/components/tools/PopOver.vue";
import PopOver2 from "@/components/tools/PopOver2.vue";

type P2PayCancellationUiState = 'SUBMITTING' | 'WAITING_CALLBACK' | 'OUTCOME_UNKNOWN'

export default /*#__PURE__*/_defineComponent({
  __name: 'WithdrawPaymentSwitching',
  setup(__props) {

const SearchIcon = require("@heroicons/vue/solid/SearchIcon");
dayjs.extend(localizedFormat);
const axios = inject("$axios") as AxiosInstance;
const store = useStore();
const route = useRoute();
const dataRows = ref<WithdrawPagination[]>([]);
const isLoaded = ref(false);
const toastConfig = ref({
  show: false,
  message: "",
  success: false,
  warning: false,
  manualClose: false,
});
const advanceFilterModal = ref(false);
const exportDialogOpen = ref(false);
const popOver = shallowRef(PopOver);
const popOver2 = shallowRef(PopOver2);
const requestedAmountLabel = translate("messages.withdrawPaymentSwitching.requestedAmount");
const p2PayCancellationUiState = ref<Record<string, P2PayCancellationUiState>>({});

const hasP2PayCancellationPermission = () =>
  store.state.user.permissions.includes('PAYMENT_CONFIG.WITHDRAW.REJECT')

const canRequestP2PayCancellationForRow = (data: WithdrawPagination) =>
  canRequestP2PayCancellation(
    data.paymentName,
    data.cmReview,
    hasP2PayCancellationPermission(),
  )

const isP2PayCancellationLocked = (transferId: unknown) =>
  (typeof transferId === 'string' || typeof transferId === 'number')
  && p2PayCancellationUiState.value[String(transferId)] !== undefined

const p2PayCancellationButtonLabel = (transferId: unknown) => {
  if (typeof transferId !== 'string' && typeof transferId !== 'number') return ''
  const state = p2PayCancellationUiState.value[String(transferId)]
  if (state === 'SUBMITTING') {
    return translate("messages.withdrawPaymentSwitching.requestingP2PayCancellation")
  }
  if (state === 'WAITING_CALLBACK') {
    return translate("messages.withdrawPaymentSwitching.p2PayCancellationWaitingCallback")
  }
  if (state === 'OUTCOME_UNKNOWN') {
    return translate("messages.withdrawPaymentSwitching.p2PayCancellationNeedsReconciliation")
  }
  return translate("messages.withdrawPaymentSwitching.requestP2PayCancellation")
}

const p2PayCmReviewReason = (data: WithdrawPagination) => {
  if (!isP2PayWithdrawPaymentName(data.paymentName)) return '-'
  const reasonCode = getP2PayCmReviewReasonCode(data.cmReview)
  if (!reasonCode) return '-'

  const reasonKey = {
    CM_ADMIN_DECISION_PENDING: 'adminDecisionReviewReason',
    CM_FINANCE_HOLD_OUTCOME_UNKNOWN: 'financeHoldReviewReason',
    CM_FINANCE_DISPATCH_OUTCOME_UNKNOWN: 'financeDispatchReviewReason',
    CM_FINANCE_RELEASE_OUTCOME_UNKNOWN: 'financeReleaseReviewReason',
    CM_PROVIDER_REQUEST_OUTCOME_UNKNOWN: 'providerOutcomeReviewReason',
    CM_PROVIDER_REQUEST_PENDING: 'providerPendingReviewReason',
  }[reasonCode]

  return translate(`messages.withdrawPaymentSwitching.${reasonKey}`)
}

const formatWithdrawAmount = (amount: number | string | null | undefined) => {
  if (amount === null || amount === undefined) return "-";

  const numericAmount = Number(amount);
  return Number.isFinite(numericAmount) ? formatNumber(numericAmount, 100) : "-";
};

const columns = ref([
  {
    label: translate("messages.withdrawPaymentSwitching.username"),
    field: "username",
    class: () => "hover:underline cursor-pointer font-bold",
    isFunction: true,
    format: (data: any) =>
      data.username ? data.username : '-',
    function: (data: any) => {
      window.open(`/players/${data.userId}`, '_blank');
      // router.push(`/players/${data.userId}`);
    },
  },
  {
    label: translate("messages.withdrawPaymentSwitching.paymentName"),
    field: "paymentName",
    align: "center",
    format: (data: any) => (data.paymentName ? data.paymentName : "-"),
  },
  {
    label: requestedAmountLabel,
    field: "amount",
    align: "center",
    sort: true,
    isSorted: null,
    format: (data: WithdrawPagination) => formatWithdrawAmount(data.amount),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.amount"),
    field: "settledAmount",
    align: "center",
    format: (data: WithdrawPagination) => formatWithdrawAmount(getWithdrawDisplayAmount(data)),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.feeAmount"),
    field: "feeAmount",
    align: "center",
    format: (data: any) => data.feeAmount ? formatNumber(data.feeAmount,100) : "-",
  },
  {
    label: translate("messages.withdrawPaymentSwitching.finalAmount"),
    field: "finalAmount",
    align: "center",
    format: (data: WithdrawPagination) => formatWithdrawAmount(getWithdrawDisplayFinalAmount(data)),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.status"),
    field: "status",
    align: "center",
    format: (data: WithdrawPagination) => {
      return getWithdrawDisplayStatus(data) || "-"
    },
    class: (data: WithdrawPagination) => {
      const displayStatus = getWithdrawDisplayStatus(data)
      if (displayStatus === 'PARTIALLY COMPLETED') {
        return 'inline-flex w-28 items-center justify-center whitespace-normal rounded-lg bg-green-100 px-2 py-1 text-center text-xs font-semibold leading-tight tracking-wide text-green-700'
      }

      let css = 'text-white font-bold text-xs rounded py-1'
      switch (displayStatus) {
        case 'PENDING':
          css += ' bg-yellow-500'
          break;
        case 'CALLBACK_SUCCEED':
          css += ' bg-blue-400'
          break;
        case 'SUBMITTED':
          css += ' bg-blue-500'
          break;
        case 'COMPLETED':
          css += ' bg-green-500'
          break;
        case 'REJECTED':
          css += ' bg-red-400'
          break;
        case 'CALLBACK_FAILED':
        css += ' bg-red-300'
        break;
        case 'APPROVING':
          css += ' bg-yellow-300'
          break;
        default:
          css += ' bg-red-500'
          break;
      }
      return css
    },
  },
  {
    label: translate("messages.withdrawPaymentSwitching.message"),
    field: "message",
    align: "center",
    isSorted: false,
    format: (data: any) => formatP2PayWithdrawMessage(data.paymentName, data.message),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.reviewReason"),
    field: "cmReview",
    align: "center",
    isSorted: false,
    format: (data: WithdrawPagination) => p2PayCmReviewReason(data),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.refCode1"),
    field: "refCode1",
    align: "center",
    format: (data: any) => data.refCode1 ? data.refCode1 : "-",
  },
  {
    label: translate("messages.withdrawPaymentSwitching.transactionId"),
    field: "transactionId",
    align: "center",
    format: (data: any) => (data.transactionId ? data.transactionId : "-"),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.refCode3"),
    field: "refCode3",
    align: "center",
    format: (data: any) => (data.refCode3 ? data.refCode3 : "-"),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.createdAt"),
    field: "createdAt",
    align: "center",
    class: () => "whitespace-nowrap",
    format: (data: any) => dayjs(data.createdAt).locale("th").format("LLL"),
  },
  {
    label: translate("messages.withdrawPaymentSwitching.requestP2PayCancellation"),
    field: "cmReview",
    align: "center",
    isFunction: true,
    class: (data: WithdrawPagination) => {
      if (!canRequestP2PayCancellationForRow(data)) return 'block'
      if (isP2PayCancellationLocked(data.id)) {
        return 'content-center bg-gray-400 text-white font-bold rounded-full cursor-wait'
      }
      return 'content-center bg-red-500 hover:bg-red-700 text-white font-bold rounded-full'
    },
    function: (data: WithdrawPagination) => {
      if (canRequestP2PayCancellationForRow(data)) {
        return onRequestP2PayCancellation(data)
      }
      return
    },
    format: (data: WithdrawPagination) => {
      if (!canRequestP2PayCancellationForRow(data)) return ''
      return p2PayCancellationButtonLabel(data.id)
    },
    permission: 'PAYMENT_CONFIG.WITHDRAW.REJECT'
  },
  {
    label: translate("messages.liveTransaction.approver"),
    field: "",
    align: "center",
    element: popOver,
    format: (data: any) =>'Approver',
  },
  {
    label: translate("messages.liveTransaction.action"),
    field: "",
    align: "center",
    isFunction: true,
    function: (data: any) => {
      checkData(data)
    },
    class: (data: any) => {
      if (!recheckAblePGW.value.includes(data.paymentName))
        return ''
      return data.status == 'SUBMITTED' ? 'content-center bg-blue-500 hover:bg-blue-700 text-white font-bold rounded-full' : ''
    },
    format: (data: any) => {
      if (!recheckAblePGW.value.includes(data.paymentName))
        return ''
      return data.status == 'SUBMITTED' ? 'Recheck' : ''
    }
  }
]);

const pageConfig = ref({
  search: undefined,
  page: 1,
  count: 0,
  pageCount: 0,
  rowsPerPage: 10,
  dataLength: 0,
  sort: [["id", "DESC"]],
});
const filterForm = ref({
  user_phone: null,
  status: ["REJECTED", "PENDING", "SUBMITTED","FAILED","CALLBACK_SUCCEED", "COMPLETED","CALLBACK_FAILED", "APPROVING"],
  approve_by: null,
  from: null,
  channel: null,
  to: null,
});
const recheckAblePGW = ref(['PAYEASY', 'PAYEASY(BANK)', 'PAYONEX', 'OXYPAY', 'SUZAKU', 'MXSOUL', 'KRUNGPAY', 'TORA246', 'BADOOPAY', 'POMPAY', 'SAWASDEEPAY'])



const filterModal = (data = { flag: 0, chanel: null, status: ["REJECTED", "PENDING", "SUBMITTED","FAILED","CALLBACK_SUCCEED", "COMPLETED","CALLBACK_FAILED", "APPROVING"] }) => {
  advanceFilterModal.value = !advanceFilterModal.value;
  if (data.flag == 1) {
    pageConfig.value.page = 1;
    filterForm.value.status = data.status;
    filterForm.value.channel = data.chanel;
    fetchPageData();
  }
};
const onSearch = (payload: { from: null; to: null }) => {
  filterForm.value.from = payload.from;
  filterForm.value.to = payload.to;
  fetchPageData();
};

const createP2PayCancellationRequestId = (): string => {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

const setP2PayCancellationUiState = (
  transferId: string,
  state?: P2PayCancellationUiState,
) => {
  const next = { ...p2PayCancellationUiState.value }
  if (state) next[transferId] = state
  else delete next[transferId]
  p2PayCancellationUiState.value = next
}

const showP2PayCancellationToast = (
  message: string,
  success: boolean,
  warning: boolean,
  manualClose: boolean,
) => {
  toastConfig.value = {
    show: true,
    message,
    success,
    warning,
    manualClose,
  }
}

const refreshAfterP2PayCancellation = async (
  message: string,
  success: boolean,
  warning: boolean,
  manualClose: boolean,
) => {
  let finalMessage = message
  try {
    await fetchPageData()
  } catch (_) {
    finalMessage = `${message} ${translate("messages.withdrawPaymentSwitching.p2PayCancellationRefreshFailed")}`
    success = false
    warning = true
    manualClose = true
  }
  showP2PayCancellationToast(finalMessage, success, warning, manualClose)
}

const handleP2PayCancellationStatus = async (
  transferId: string,
  status: unknown,
): Promise<P2PayCancellationOutcome> => {
  const outcome = getP2PayCancellationOutcome(status)

  if (outcome === 'ACCEPTED') {
    setP2PayCancellationUiState(transferId, 'WAITING_CALLBACK')
    await refreshAfterP2PayCancellation(
      translate("messages.withdrawPaymentSwitching.p2PayCancellationAccepted"),
      true,
      false,
      false,
    )
    return outcome
  }

  if (outcome === 'UNKNOWN') {
    setP2PayCancellationUiState(transferId, 'OUTCOME_UNKNOWN')
    await refreshAfterP2PayCancellation(
      translate("messages.withdrawPaymentSwitching.p2PayCancellationUnknown"),
      false,
      true,
      true,
    )
    return outcome
  }

  if (outcome === 'REJECTED') {
    await refreshAfterP2PayCancellation(
      translate("messages.withdrawPaymentSwitching.p2PayCancellationRejected"),
      false,
      true,
      true,
    )
    return outcome
  }

  return outcome
}

const onRequestP2PayCancellation = async (data: WithdrawPagination) => {
  if (!canRequestP2PayCancellationForRow(data)) return

  const transferId = Number(data.id)
  if (
    !Number.isSafeInteger(transferId)
    || transferId <= 0
    || isP2PayCancellationLocked(transferId)
  ) return
  const transferKey = String(transferId)

  if (!confirm(translate("messages.withdrawPaymentSwitching.confirmP2PayCancellation"))) return

  setP2PayCancellationUiState(transferKey, 'SUBMITTING')
  let preserveLock = false

  try {
    const requestId = createP2PayCancellationRequestId()
    const response = await axios.post(
      `${process.env.VUE_APP_API_ENDPOINT}/admin/api/v1/be-payment/withdraw/p2pay/cancel`,
      buildP2PayCancellationRequest(transferId, requestId),
    )
    const outcome = await handleP2PayCancellationStatus(transferKey, response.status)
    preserveLock = outcome === 'ACCEPTED' || outcome === 'UNKNOWN'
    if (outcome === 'FAILED') {
      showP2PayCancellationToast(
        translate("messages.withdrawPaymentSwitching.p2PayCancellationFailed"),
        false,
        false,
        true,
      )
    }
  } catch (error: any) {
    const status = error?.response?.status
    const outcome = await handleP2PayCancellationStatus(transferKey, status)
    preserveLock = outcome === 'UNKNOWN'
    if (outcome === 'FAILED') {
      showP2PayCancellationToast(
        translate("messages.withdrawPaymentSwitching.p2PayCancellationFailed"),
        false,
        false,
        true,
      )
    }
  } finally {
    if (!preserveLock) setP2PayCancellationUiState(transferKey)
  }
}

const checkData = async (payload: any) => {
    const body = {
      operatorId: store.state.selectedOperator.operatorId,
      userId: payload.userId,
      transferId: Number(payload.id),
    };
    try {
      const type = payload.transferType === "DEPOSIT"? "deposit":"withdraw";

      const { data } = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/admin/api/v1/be-payment/payment/${type}/recheck`,
          body
      );
      console.log("data: ",data)
      if(data.status !== "SUBMITTED"){
        alert("Change status Success");
      }else{
        alert("status can't change please check payment gateway backoffice")
      }
      fetchPageData();
    } catch (err) {
      alert("❌ Fetch error:");
    } finally {
    }
};

const doSort = (attName: string) => {
  const index = columns.value.findIndex((column) => column.label === attName);


  columns.value[index].isSorted = columns.value[index].isSorted==true? false:columns.value[index].isSorted==false?null:true;
  if(columns.value[index].isSorted == null) pageConfig.value.sort = [["id", "DESC"]]
  else {
    const { sort } = pageConfig.value;
    const mode = sort[0][1];
    if (attName === "Date Requested") attName = "seq_num";
    if (attName === "Date Approved") attName = "approve_at";
    if (attName === "Approved By") attName = "approve_by";
    if (attName === "Status") attName = "approve_status";
    if (attName === requestedAmountLabel) attName = "amount";
    sort[0] = [attName.toLowerCase(), mode === "DESC" ? "ASC" : "DESC"];
  }


  fetchPageData();
};

const fetchPageData = async () => {
  isLoaded.value = false
  const { page, rowsPerPage, sort, search } = pageConfig.value;
  const { user_phone, status, approve_by, from, to, channel } = filterForm.value;
  const payload: any = {
    operatorId: store.state.selectedOperator.operatorId,
    page: page,
    pageSize: rowsPerPage,
    userPhone: search? search: user_phone ? user_phone : undefined,
    status: status ? status : ["REJECTED", "PENDING", "SUBMITTED","FAILED","CALLBACK_SUCCEED", "COMPLETED","CALLBACK_FAILED", "APPROVING"],
    includeCmReview: true,
    approve_by: approve_by || undefined,
    channel: channel || undefined,
    startDate: dayjs(from).format('YYYY-MM-DD') || null,
    endDate: dayjs(to).format('YYYY-MM-DD') || null,
    sort: sort,
  };
  if (!from) delete payload.startDate;
  if (!to) delete payload.endDate;
  const url = `${process.env.VUE_APP_API_ENDPOINT}/admin/api/v1/be-payment/withdraw/pagination`;
  const { result: data } = await axios
    .post(url, payload)
    .then(({ data }) => data);

  pageConfig.value.dataLength = data.length;

  if (data.length > 0) dataRows.value = await Promise.all(data.map(d => ({...d, _useTransfer: true}))) ;
  else dataRows.value = [];
  pageConfig.value = {
    ...pageConfig.value,
    // count,
    // pageCount: Math.ceil(count / rowsPerPage),
  };
  /** update url to persist page config */
  window.history.replaceState(
    {},
    document.title,
    `?${stringify({ page, rowsPerPage })}`
  );
  isLoaded.value = true;
};


onMounted(() => {
  pageConfig.value.page = Number(route.query.page) || 1;
  pageConfig.value.rowsPerPage = Number(route.query.rowsPerPage) || 10;
  fetchPageData();
});
/** fetch data when current page was changed */
watch(
  () => pageConfig.value.page,
  () => fetchPageData()
);
/** fetch data when rows per page was changed */
watch(
  () => pageConfig.value.rowsPerPage,
  () => {
    pageConfig.value.page = 1;
    fetchPageData();
  }
);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Navbar, {
      title: _ctx.$t('messages.withdrawPaymentSwitching.withdrawPaymentSwitching')
    }, null, 8, ["title"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _hoisted_3,
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(SearchIcon), {
              class: "h-5 w-5",
              "aria-hidden": "true"
            })
          ]),
          _withDirectives(_createElementVNode("input", {
            id: "search",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pageConfig.value.search) = $event)),
            class: "block w-full bg-white py-2 pl-10 focus:bg-white pr-3 border border-transparent rounded-md leading-5 text-gray-900 placeholder-gray-500 focus:ring-offset-blue-600 focus:ring-white focus:border-white sm:text-sm",
            placeholder: _ctx.$t('messages.depositPaymentSwitching.searchUsername'),
            onInput: _cache[1] || (_cache[1] = ($event: any) => (fetchPageData(true))),
            type: "search",
            name: "search"
          }, null, 40, _hoisted_6), [
            [_vModelText, pageConfig.value.search]
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(DateSelector, { onSearch: onSearch }),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("button", {
            type: "button",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (advanceFilterModal.value = true)),
            class: "md:ml-3 inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500"
          }, [
            _createVNode(_component_font_awesome_icon, {
              class: "text-white mr-2",
              icon: ['far', 'filter'],
              transform: ""
            }),
            _createTextVNode(" " + _toDisplayString(_ctx.$t("messages.withdrawPaymentSwitching.advanceFilter")), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("button", {
            type: "button",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (exportDialogOpen.value = true)),
            class: "ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500"
          }, [
            _createVNode(_component_font_awesome_icon, {
              class: "mr-2",
              icon: ['far', 'file-export']
            }),
            _createTextVNode(" " + _toDisplayString(_ctx.$t("messages.dailyReport.exportReport")), 1)
          ])
        ])
      ])
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", _hoisted_11, [
        _createVNode(_component_font_awesome_icon, {
          class: "text-gray-500",
          icon: ['fas', 'circle-notch'],
          transform: "grow-6",
          spin: ""
        })
      ])
    ], 512), [
      [_vShow, !isLoaded.value]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_12, [
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("div", _hoisted_14, [
          _createVNode(DataTable, {
            columns: columns.value,
            data: dataRows.value,
            onSort: doSort
          }, null, 8, ["columns", "data"]),
          _createVNode(Pagination, {
            config: pageConfig.value,
            "onUpdate:config": _cache[4] || (_cache[4] = ($event: any) => ((pageConfig).value = $event))
          }, null, 8, ["config"])
        ])
      ])
    ], 512), [
      [_vShow, isLoaded.value]
    ]),
    _createVNode(Toast, {
      show: toastConfig.value.show,
      "onUpdate:show": _cache[5] || (_cache[5] = ($event: any) => ((toastConfig.value.show) = $event)),
      message: toastConfig.value.message,
      success: toastConfig.value.success,
      warning: toastConfig.value.warning,
      manual_close: toastConfig.value.manualClose
    }, null, 8, ["show", "message", "success", "warning", "manual_close"]),
    _createVNode(WithdrawPaymentSwitchingFilterDialog, {
      class: "z-10",
      show: advanceFilterModal.value,
      "onUpdate:show": _cache[6] || (_cache[6] = ($event: any) => ((advanceFilterModal).value = $event)),
      form: filterForm.value,
      "onUpdate:form": _cache[7] || (_cache[7] = ($event: any) => ((filterForm).value = $event)),
      onSubmit: filterModal
    }, null, 8, ["show", "form"]),
    _createVNode(ExportTransactionDialog, {
      isOpen: exportDialogOpen.value,
      "onUpdate:isOpen": _cache[8] || (_cache[8] = ($event: any) => ((exportDialogOpen).value = $event)),
      type: "WITHDRAW"
    }, null, 8, ["isOpen"])
  ], 64))
}
}

})